import { PhotoDraft, PhotoSection, SECTIONS, productKeyOf } from "./photoModel";
import { ProductInfo } from "./useProductValidation";
import { ItemState } from "./useUploadQueue";

/**
 * The receipt for one upload run.
 *
 * It answers, in the order the uploader needs them: did it work, what did not and what do
 * I do about it, and where did the photos actually land. Everything here is derived from
 * current state rather than snapshotted, so retrying a failed photo updates the receipt
 * instead of leaving a stale one on screen.
 */

/** Why a photo in the batch was never sent. */
export type SkipReason = "no-direction" | "product-not-found" | "not-sent";

export interface ReportFile {
  id: string;
  barcode: string;
  matCode: string;
  section: PhotoSection;
  /** The name the photo carries in storage — what a later lookup will find. */
  storedName: string;
  originalName: string;
  key: string;
  error?: string;
  reason?: SkipReason;
}

export interface ReportSection {
  section: PhotoSection;
  files: ReportFile[];
}

export interface ReportProduct {
  productKey: string;
  /** `<barcode>_<matcode>` — the id `/product/:id/show` expects. */
  productId: string;
  barcode: string;
  matCode: string;
  name?: string;
  sections: ReportSection[];
  photos: number;
}

export interface UploadReport {
  total: number;
  succeeded: number;
  failed: ReportFile[];
  skipped: ReportFile[];
  products: ReportProduct[];
  /** True when every photo in the batch reached storage. */
  complete: boolean;
}

export interface BuildReportInput {
  drafts: PhotoDraft[];
  uploads: Record<string, ItemState>;
  derivedNames: Record<string, { fileName: string; key: string }>;
  productInfo: Record<string, ProductInfo>;
}

export const productIdOf = (barcode: string, matCode: string) => `${barcode}_${matCode}`;

function skipReasonOf(draft: PhotoDraft, productInfo: Record<string, ProductInfo>): SkipReason {
  if (!draft.direction) return "no-direction";
  if (productInfo[productKeyOf(draft.barcode, draft.matCode)]?.status !== "valid") {
    return "product-not-found";
  }
  return "not-sent";
}

export function buildReport({
  drafts,
  uploads,
  derivedNames,
  productInfo,
}: BuildReportInput): UploadReport {
  const failed: ReportFile[] = [];
  const skipped: ReportFile[] = [];
  const byProduct = new Map<string, Map<PhotoSection, ReportFile[]>>();

  const fileOf = (draft: PhotoDraft, extra: Partial<ReportFile> = {}): ReportFile => {
    const derived = derivedNames[draft.id];
    return {
      id: draft.id,
      barcode: draft.barcode,
      matCode: draft.matCode,
      section: draft.section,
      storedName: derived?.fileName ?? draft.origin.fileName,
      originalName: draft.origin.fileName,
      key: derived?.key ?? "",
      ...extra,
    };
  };

  for (const draft of drafts) {
    const status = uploads[draft.id]?.status;
    if (status === "success") {
      const productKey = productKeyOf(draft.barcode, draft.matCode);
      const sections = byProduct.get(productKey) ?? new Map<PhotoSection, ReportFile[]>();
      byProduct.set(productKey, sections);
      const files = sections.get(draft.section) ?? [];
      sections.set(draft.section, files);
      files.push(fileOf(draft));
    } else if (status === "failed") {
      failed.push(fileOf(draft, { error: uploads[draft.id]?.error }));
    } else {
      skipped.push(fileOf(draft, { reason: skipReasonOf(draft, productInfo) }));
    }
  }

  const products: ReportProduct[] = Array.from(byProduct.keys())
    .sort()
    .map((productKey) => {
      const [barcode, matCode] = productKey.split("/");
      const sectionMap = byProduct.get(productKey)!;
      const sections = SECTIONS.filter((s) => sectionMap.has(s)).map((section) => ({
        section,
        files: [...(sectionMap.get(section) ?? [])].sort((a, b) =>
          a.storedName.localeCompare(b.storedName)
        ),
      }));
      return {
        productKey,
        productId: productIdOf(barcode, matCode),
        barcode,
        matCode,
        name: productInfo[productKey]?.status === "valid" ? productInfo[productKey]?.name : undefined,
        sections,
        photos: sections.reduce((n, s) => n + s.files.length, 0),
      };
    });

  const succeeded = products.reduce((n, p) => n + p.photos, 0);
  return {
    total: drafts.length,
    succeeded,
    failed,
    skipped,
    products,
    complete: drafts.length > 0 && succeeded === drafts.length,
  };
}

/** True once an upload run has produced at least one settled result. */
export function hasSettled(uploads: Record<string, ItemState>): boolean {
  return Object.values(uploads).some((u) => u.status === "success" || u.status === "failed");
}

/** What an upload run is about to do, answered before the user commits to it. */
export interface Preflight {
  totalPhotos: number;
  totalMatcodes: number;
  sendPhotos: number;
  sendMatcodes: number;
  /** Photos that will replace one already stored under the same key. */
  overwrites: number;
  noDirection: number;
  noProduct: number;
  alreadyUploaded: number;
}

export interface BuildPreflightInput extends BuildReportInput {
  conflicts: Set<string>;
}

/**
 * The confirm dialog is the last point where the decision can change, so it has to carry
 * the facts itself — how many photos are going, how many will overwrite something already
 * stored, and what is being held back. Reading them off the page behind the dialog is not
 * an option once the dialog covers it.
 */
export function buildPreflight({
  drafts,
  uploads,
  derivedNames,
  productInfo,
  conflicts,
}: BuildPreflightInput): Preflight {
  const allMatcodes = new Set<string>();
  const sendMatcodes = new Set<string>();
  let sendPhotos = 0;
  let overwrites = 0;
  let noDirection = 0;
  let noProduct = 0;
  let alreadyUploaded = 0;

  for (const draft of drafts) {
    const productKey = productKeyOf(draft.barcode, draft.matCode);
    allMatcodes.add(productKey);

    if (uploads[draft.id]?.status === "success") {
      alreadyUploaded += 1;
    } else if (!draft.direction) {
      noDirection += 1;
    } else if (productInfo[productKey]?.status !== "valid") {
      noProduct += 1;
    } else {
      sendPhotos += 1;
      sendMatcodes.add(productKey);
      if (conflicts.has(derivedNames[draft.id]?.key ?? "")) overwrites += 1;
    }
  }

  return {
    totalPhotos: drafts.length,
    totalMatcodes: allMatcodes.size,
    sendPhotos,
    sendMatcodes: sendMatcodes.size,
    overwrites,
    noDirection,
    noProduct,
    alreadyUploaded,
  };
}
